'use client';

import clsx from 'clsx';
import { Heading2 } from '@/components/section/headings';
import type { DeliveryBullet } from '@/constants/homeMarketing';
import { MarketingHeroIllustration } from '@/assets/icons';
import { H3, Img, Li, P, Ul, simpleCard } from '@/components/ui';

export type DeliveryEngagementSectionProps = {
  title: string;
  subheading: string;
  imageSrc?: string;
  imageAlt: string;
  illustrationPath?: string;
  bullets: readonly DeliveryBullet[];
  sectionId?: string;
  className?: string;
};

/**
 * Split narrative for delivery models — aligns with IT services / staff augmentation positioning.
 */
const DeliveryEngagementSection: React.FC<DeliveryEngagementSectionProps> = ({
  title,
  subheading,
  imageSrc,
  imageAlt,
  illustrationPath,
  bullets,
  sectionId,
  className,
}) => {
  return (
    <section
      id={sectionId}
      className={clsx('container-section', className)}
      aria-labelledby="delivery-heading"
    >
      <div className="page-hero-split">
        <div className="order-2 md:order-1 flex flex-col justify-center text-center md:text-left gap-6">
          <div id="delivery-heading">
            <Heading2
              title={title}
              subtitle={subheading}
              titleClassName="text-prose"
              subtitleClassName="mt-3 text-prose-muted"
            />
          </div>
          <Ul className="m-0 list-none space-y-4 p-0 text-left">
            {bullets.map((b) => (
              <Li key={b.title} className={simpleCard('p-4 sm:p-5')}>
                <H3 className="m-0 text-base font-semibold text-prose">{b.title}</H3>
                <P className="m-0 mt-2 text-sm leading-relaxed text-prose-muted">{b.text}</P>
              </Li>
            ))}
          </Ul>
        </div>
        <div className="page-hero-media order-1 md:order-2">
          <div className="page-hero-media-frame">
            {illustrationPath ? (
              <MarketingHeroIllustration path={illustrationPath} className="h-auto w-full" />
            ) : imageSrc ? (
              <Img
                src={imageSrc}
                alt={imageAlt}
                width={640}
                height={420}
                className="h-auto w-full object-cover"
                sizes="(max-width: 768px) 100vw, 480px"
              />
            ) : null}
          </div>
        </div>
      </div>
    </section>
  );
};

export default DeliveryEngagementSection;
